import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers'
import {
    ApartmentContract,
    ApartmentContractType,
} from '../models/Company/types'
import { updateContractRequest } from './useCompanyContracts'

const QUERY_KEY = ['orgCompanyContracts']
const ANALYTICS_QUERY_KEY = ['orgContractAnalytics']

export interface ContractAnalytics {
    total: number
    active: number
    ending_soon: number
    auto_renew_notice: number
    expired: number
    no_dates: number
}

export interface OrgContractParams {
    organization?: number | null
}

export const useOrgCompanyContracts = (params?: OrgContractParams) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Build query string for list endpoints (includes all filters)
    const buildQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only, no list filters)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    // Get all company contracts for organization
    const {
        data: contracts,
        isLoading: isLoadingContracts,
        error: errorContracts,
        refetch: refetchContracts,
    } = useQuery<AxiosResponse<ApartmentContract[]>, Error>({
        queryKey: [...QUERY_KEY, params?.organization],
        queryFn: () =>
            axiosInstance.get(
                `/company/apartment_contract/${buildQueryString()}`,
            ),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    // Get contract analytics for organization
    const { data: analytics, isLoading: isLoadingAnalytics } = useQuery<
        AxiosResponse<ContractAnalytics>,
        Error
    >({
        queryKey: [...ANALYTICS_QUERY_KEY, params?.organization],
        queryFn: () =>
            axiosInstance.get(
                `/company/apartment_contract/analytics/${buildQueryString()}`,
            ),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    // Create/upload a new contract. Pass apartment_ids to attach to selected properties.
    const createContract = useMutation({
        mutationFn: async ({
            file,
            apartment_ids,
            contract_type,
            organization_contract_type,
            organization_certificate_type,
        }: {
            file: File
            apartment_ids: number[]
            contract_type?: ApartmentContractType
            organization_contract_type?: number | null
            organization_certificate_type?: number | null
        }) => {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('apartment_ids', JSON.stringify(apartment_ids))
            if (contract_type) {
                formData.append('contract_type', contract_type)
            }
            if (organization_contract_type) {
                formData.append(
                    'organization_contract_type',
                    String(organization_contract_type),
                )
            }
            if (organization_certificate_type) {
                formData.append(
                    'organization_certificate_type',
                    String(organization_certificate_type),
                )
            }

            return axiosInstance.post<ApartmentContract>(
                `/company/apartment_contract/${buildQueryString()}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload contract'
            toast.error(errorMessage)
        },
    })

    // Upload multiple contracts with same apartment_ids
    const uploadContracts = async (
        files: File[],
        apartment_ids: number[],
        contract_type?: ApartmentContractType,
        organization_contract_type?: number | null,
        organization_certificate_type?: number | null,
    ) => {
        const results = await Promise.allSettled(
            files.map((file) =>
                createContract.mutateAsync({
                    file,
                    apartment_ids,
                    contract_type,
                    organization_contract_type,
                    organization_certificate_type,
                }),
            ),
        )
        const successCount = results.filter((r) => r.status === 'fulfilled')
            .length
        const failCount = results.filter((r) => r.status === 'rejected').length
        if (failCount > 0 && successCount > 0) {
            toast.warning(
                `Uploaded ${successCount} contract(s), ${failCount} failed`,
            )
        } else if (failCount > 0 && successCount === 0) {
            toast.error('Failed to upload contracts')
        }
        return results
    }

    // Process/analyze a contract with AI
    const processContract = useMutation({
        mutationFn: async (contractId: number) => {
            setProcessingIds((prev) => new Set(prev).add(contractId))
            return axiosInstance.post(
                `/company/apartment_contract/${contractId}/process_contract/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract analyzed successfully')
        },
        onError: (error: any, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze contract'
            toast.error(errorMessage)
        },
    })

    // Update a contract
    const updateContract = useMutation({
        mutationFn: async ({
            contractId,
            data,
        }: {
            contractId: number
            data: updateContractRequest
        }) => {
            return axiosInstance.patch<ApartmentContract>(
                `/company/apartment_contract/${contractId}/${buildDetailQueryString()}`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['contractAlerts'] })
            toast.success('Contract updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update contract'
            toast.error(errorMessage)
        },
    })

    // Delete a contract
    const deleteContract = useMutation({
        mutationFn: async (contractId: number) => {
            return axiosInstance.delete(
                `/company/apartment_contract/${contractId}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete contract'
            toast.error(errorMessage)
        },
    })

    return {
        contracts: contracts?.data || [],
        isLoadingContracts,
        errorContracts,
        refetchContracts,
        analytics: analytics?.data || null,
        isLoadingAnalytics,
        createContract,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        isUploading: createContract.isLoading,
        isProcessing: processContract.isLoading,
        isUpdating: updateContract.isLoading,
        isDeleting: deleteContract.isLoading,
        processingIds,
    }
}

export type UpdateContractRequest = updateContractRequest
